import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSuperGetOrganizationsQuery, useSuperRemoveMemberMutation } from '../api/superAdminApi';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate } from '../../../shared/utils/date';
import {
  ArrowLeft,
  Users,
  Search,
  CheckCircle2,
  Crown,
  Shield,
  User,
  X
} from 'lucide-react';
import { Dropdown } from '@/shared/components/atoms/Dropdown/Dropdown';

/* ── Role styling ─────────────────────────────────────────────── */
const getRoleConfig = (role: string) => {
  switch (role.toLowerCase()) {
    case 'owner':
      return { bg: 'rgba(239, 68, 68, 0.12)', text: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.25)', icon: Crown, label: 'Owner' };
    case 'admin':
      return { bg: 'rgba(249, 115, 22, 0.12)', text: '#f97316', border: '1px solid rgba(249, 115, 22, 0.25)', icon: Shield, label: 'Admin' };
    default:
      return { bg: 'rgba(56, 189, 248, 0.12)', text: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.25)', icon: User, label: role || 'Member' };
  }
};

const getInitials = (nameOrEmail: string) =>
  nameOrEmail.charAt(0).toUpperCase();

import { SuperOrgMemberSkeleton } from '@/features/admin/components/loaders/MemberSkeletons';

export function SuperOrgMembers() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { data: response, isLoading, refetch } = useSuperGetOrganizationsQuery({ limit: 1000 });
  const [removeMember] = useSuperRemoveMemberMutation();

  const [search, setSearch] = useState('');
  const [sortFilter, setSortFilter] = useState('newest');

  const org = response?.data?.data?.find((o: any) => o.id === orgId);
  const members: any[] = org?.members || [];

  const handleRemove = (memberId: string, memberEmail: string, role: string) => {
    if (role === 'Owner') {
      Swal.fire({
        title: 'Action Denied',
        text: 'You cannot remove the primary owner of the organization.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        background: '#0d1117',
        color: '#f0f8ff'
      });
      return;
    }

    Swal.fire({
      title: 'Remove Member?',
      text: `Are you sure you want to remove ${memberEmail} from this organization?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: 'var(--admin-card-subtext)',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel',
      background: '#0d1117',
      color: '#f0f8ff'
    }).then((result) => {
      if (result.isConfirmed && orgId) {
        removeMember({ orgId, adminId: memberId }).unwrap()
          .then(() => {
            Swal.fire({
              title: 'Removed!',
              text: `${memberEmail} has been removed.`,
              icon: 'success',
              confirmButtonColor: '#38bdf8',
              background: '#0d1117',
              color: '#f0f8ff'
            });
            refetch();
          })
          .catch((err) => {
            Swal.fire({
              title: 'Error',
              text: err?.data?.message || 'Failed to remove member.',
              icon: 'error',
              confirmButtonColor: '#ef4444',
              background: '#0d1117',
              color: '#f0f8ff'
            });
          });
      }
    });
  };

  // ── Filter & Sort Logic ──
  const filteredMembers = members
    .filter((m) => {
      const q = search.toLowerCase();
      return (
        !search ||
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.role || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const dateA = new Date(a.joined_at || 0).getTime();
      const dateB = new Date(b.joined_at || 0).getTime();
      return sortFilter === 'newest' ? dateB - dateA : dateA - dateB;
    });

  const maxMembers = org?.subscription?.max_members ?? 1;
  const seatsLeft =
    org?.subscription?.max_members === 999999
      ? 'Unlimited'
      : Math.max(0, maxMembers - members.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        boxSizing: 'border-box',
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* ── Page Heading with ArrowLeft Back Button (matching Session Analytics) ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            type="button"
            onClick={() => navigate('/admin/organizations')}
            style={{
              padding: '8px',
              borderRadius: '0',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              color: 'var(--admin-card-text)',
              border: '1px solid var(--admin-card-border)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
            className="hover:bg-white/[0.08] transition-colors"
            title="Back to Organizations"
          >
            <ArrowLeft size={18} />
          </button>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h1 style={{ 
              fontFamily: "'Space Grotesk', sans-serif",
              color: 'var(--admin-card-text)', 
              fontSize: '37.9px', 
              fontWeight: 700, 
              margin: '0 0 6px', 
              letterSpacing: '-0.76px',
              lineHeight: '38px'
            }}>
              {isLoading ? 'Loading Members…' : `${org?.name ?? 'Organization'} Members`}
            </h1>
            <p style={{ 
              fontFamily: "'Inter', sans-serif",
              color: '#7C8891', 
              fontSize: '14px', 
              margin: 0, 
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0'
            }}>
              Inspect and manage organization members, roles, and seat allocations.
            </p>
          </div>
        </div>

        {/* Right Section Stats: Seats Used & Seats Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ color: 'var(--admin-card-subtext)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>
              Seats Used
            </span>
            <span style={{ color: 'var(--admin-card-text)', fontSize: '24px', fontWeight: 800 }}>
              {members.length}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ color: 'var(--admin-card-subtext)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>
              Seats Left
            </span>
            <span style={{ color: '#38bdf8', fontSize: '24px', fontWeight: 800 }}>
              {seatsLeft}
            </span>
          </div>
        </div>
      </div>

      {/* ── Toolbar (Search + Sort) ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        {/* Search Input */}
        <div style={{ position: 'relative', flex: '1 1 300px' }}>
          <Search size={13} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-card-subtext)' }} />
          <input
            type="text"
            placeholder="Search members by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'var(--color-bg-elevated, rgba(255,255,255,0.03))',
              border: '1px solid var(--admin-card-border)',
              borderRadius: '0',
              padding: '9px 16px 9px 40px',
              color: 'var(--admin-card-text)',
              fontSize: '13px',
              fontFamily: "'Inter', sans-serif",
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Sort Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '140px' }}>
            <Dropdown
              value={sortFilter}
              onChange={setSortFilter}
              options={[
                { value: 'newest', label: 'Newest first' },
                { value: 'oldest', label: 'Oldest first' }
              ]}
              className="w-full kb-dropdown-tall"
            />
          </div>
        </div>
      </div>

      {/* ── Member List Container ── */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--admin-card-border)',
        overflow: 'hidden',
        backgroundColor: 'var(--admin-card-bg)',
        borderRadius: '0'
      }}>
        {isLoading ? (
          <>
            <SuperOrgMemberSkeleton />
            <SuperOrgMemberSkeleton />
            <SuperOrgMemberSkeleton />
            <SuperOrgMemberSkeleton />
          </>
        ) : filteredMembers.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--admin-card-subtext)', margin: 0 }}>
              No members found.
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredMembers.map((member, idx) => {
              const roleConfig = getRoleConfig(member.role);
              const RoleIcon = roleConfig.icon;
              const initials = getInitials(member.name || member.email);

              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 20px',
                    gap: '12px',
                    flexWrap: 'wrap',
                    borderBottom: idx < filteredMembers.length - 1 ? '1px solid var(--admin-card-border)' : 'none',
                    transition: 'background 0.15s'
                  }}
                  className="hover:bg-white/[0.03]"
                >
                  {/* Left Column: Avatar + Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: '1 1 200px' }}>
                    <div style={{
                      width: '34px',
                      height: '34px',
                      backgroundColor: 'var(--color-bg-elevated, rgba(255,255,255,0.05))',
                      color: 'var(--admin-card-text)',
                      border: '1px solid var(--admin-card-border)',
                      borderRadius: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: 700,
                      flexShrink: 0
                    }}>
                      {initials}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)' }}>
                          {member.name || member.email}
                        </span>
                        <span style={{
                          backgroundColor: roleConfig.bg,
                          color: roleConfig.text,
                          border: roleConfig.border,
                          fontSize: '9px',
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: '0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <RoleIcon size={10} />
                          {roleConfig.label}
                        </span>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--admin-card-subtext)' }}>
                        {member.email} &bull; Joined {formatDate(member.joined_at)}
                      </span>
                    </div>
                  </div>

                  {/* Right Column: Status + Remove Button */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#10b981',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      <CheckCircle2 size={13} /> Active
                    </span>

                    <button
                      type="button"
                      onClick={() => handleRemove(member.id, member.email, roleConfig.label)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '6px 12px',
                        borderRadius: '0',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                        color: '#ef4444',
                        fontSize: '11px',
                        fontWeight: 700,
                        fontFamily: "'Inter', sans-serif",
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        transition: 'all 0.15s'
                      }}
                      className="hover:bg-red-500/20"
                      title="Remove Member"
                    >
                      <X size={13} /> Remove
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {/* ── Footer ── */}
      {!isLoading && filteredMembers.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '12px',
          color: 'var(--admin-card-subtext)',
          fontSize: '12px'
        }}>
          <Users size={14} />
          <span>
            Showing <strong style={{ color: 'var(--admin-card-text)' }}>{filteredMembers.length}</strong> member{filteredMembers.length !== 1 ? 's' : ''} in <strong style={{ color: 'var(--admin-card-text)' }}>{org?.name}</strong>
          </span>
        </div>
      )}
    </motion.div>
  );
}
